import { useCallback, useEffect, useState } from 'react';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import {
  Button, Chip, Empty, Input, Loading, Modal,
} from '../../components/Ui';
import { useToast } from '../../context/ToastContext';

const ICONS = ['🛒', '🌾', '🥛', '🍞', '🥬', '🍎', '🧴', '🧹', '🍪', '☕', '👕', '👞', '📱', '🔧', '💊', '🍗', '🎁', '⭐'];
const BLANK = { name: '', icon: '', image: '', parent: null, forKind: 'both' };

/**
 * The shop's own categories - its shelves.
 *
 * A partner builds them here: a category, sub-categories under it, an icon or
 * a photo for each. They can also tick the ones KAAMWALA suggests for their
 * trade and get their own copy. None of this touches KAAMWALA's categories;
 * it is how customers walk this one shop.
 */
const MyCategoriesPage = () => {
  const toast = useToast();
  const [tree, setTree] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);
  const [master, setMaster] = useState([]);
  const [picked, setPicked] = useState([]);

  const load = useCallback(() => {
    api.partners.myCategories().then((r) => setTree(r.data || [])).catch(() => setTree([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPicker = async () => {
    setPicked([]);
    setPicker(true);
    try { setMaster((await api.partners.masterCategories()).data || []); } catch (e) { setMaster([]); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(), icon: draft.icon, image: draft.image, parent: draft.parent, forKind: draft.forKind,
      };
      if (draft._id) await api.partners.updateCategory(draft._id, body);
      else await api.partners.createCategory(body);
      toast.success(draft._id ? 'Category updated' : 'Category added');
      setDraft(null);
      load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${row.name}"?${row.children && row.children.length ? ' Its sub-categories go too.' : ''} Items on these shelves stay in your catalogue, unfiled.`)) return;
    try { await api.partners.removeCategory(row._id); load(); } catch (err) { toast.error(err.message); }
  };

  const adopt = async () => {
    try {
      const res = await api.partners.adoptCategories({ ids: picked });
      setPicker(false);
      toast.success(res.message);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const Thumb = ({ row, size = 40 }) => (row.image ? (
    <img src={fileUrl(row.image)} alt="" style={{ width: size, height: size, borderRadius: 10, objectFit: 'cover' }} />
  ) : (
    <span style={{
      width: size, height: size, borderRadius: 10, background: 'var(--primary-light)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size / 2,
    }}
    >
      {row.icon || '🗂️'}
    </span>
  ));

  return (
    <div style={{ maxWidth: 820 }}>
      <PageHeader
        title="My categories"
        subtitle="How customers walk your shop"
        back
        right={(
          <div className="row" style={{ gap: 8 }}>
            <Button title="Pick suggested" variant="light" size="sm" onClick={openPicker} />
            <Button title="+ New category" size="sm" onClick={() => setDraft({ ...BLANK })} />
          </div>
        )}
      />

      {tree === null ? <Loading /> : !tree.length ? (
        <Empty
          emoji="🗂️"
          title="No categories yet"
          text="Group what you sell - Dairy, Snacks, Winter wear - so customers can find it."
          actionLabel="Pick suggested categories"
          onAction={openPicker}
        />
      ) : tree.map((row) => (
        <div className="card sh-sm" key={row._id} style={{ marginBottom: 12 }}>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            <Thumb row={row} />
            <div className="grow">
              <div className="t-title">{row.name}</div>
              <div className="t-tiny">
                {row.itemCount} item{row.itemCount === 1 ? '' : 's'}
                {row.children.length ? ` · ${row.children.length} sub-categories` : ''}
              </div>
            </div>
            <button type="button" className="link-btn" onClick={() => setDraft({ ...BLANK, ...row })}>Edit</button>
            <button type="button" className="link-btn" onClick={() => remove(row)}>Remove</button>
          </div>

          {row.children.map((kid) => (
            <div className="row" key={kid._id} style={{ gap: 12, alignItems: 'center', marginTop: 10, paddingTop: 10, paddingLeft: 16, borderTop: '1px solid var(--line)' }}>
              <Thumb row={kid} size={28} />
              <div className="grow">
                <div className="t-body">{kid.name}</div>
                <div className="t-tiny">{kid.itemCount} item{kid.itemCount === 1 ? '' : 's'}</div>
              </div>
              <button type="button" className="link-btn" onClick={() => setDraft({ ...BLANK, ...kid })}>Edit</button>
              <button type="button" className="link-btn" onClick={() => remove(kid)}>Remove</button>
            </div>
          ))}

          <button type="button" className="link-btn" style={{ marginTop: 10 }} onClick={() => setDraft({ ...BLANK, parent: row._id })}>
            + Add a sub-category
          </button>
        </div>
      ))}

      <Modal
        open={Boolean(draft)}
        onClose={() => setDraft(null)}
        title={draft && draft._id ? 'Edit category' : draft && draft.parent ? 'New sub-category' : 'New category'}
      >
        {draft ? (
          <form onSubmit={save}>
            <Input
              label="Name"
              placeholder="Dairy, Snacks, Winter wear…"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              required
            />
            <label className="field-label">Icon</label>
            <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
              {ICONS.map((i) => (
                <Chip key={i} label={i} active={draft.icon === i} onClick={() => setDraft({ ...draft, icon: draft.icon === i ? '' : i })} />
              ))}
            </div>
            <ImageUploader
              label="Photo (optional)"
              hint="Shown instead of the icon on your shop page."
              folder="category"
              multiple={false}
              value={draft.image}
              onChange={(url) => setDraft({ ...draft, image: url })}
            />
            <label className="field-label">Used for</label>
            <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
              {[['both', 'Everything'], ['product', 'Products'], ['service', 'Services']].map(([v, l]) => (
                <Chip key={v} label={l} active={draft.forKind === v} onClick={() => setDraft({ ...draft, forKind: v })} />
              ))}
            </div>
            <div className="row" style={{ gap: 10 }}>
              <Button title="Save" loading={saving} type="submit" />
              <Button title="Cancel" variant="light" onClick={() => setDraft(null)} />
            </div>
          </form>
        ) : null}
      </Modal>

      <Modal open={picker} onClose={() => setPicker(false)} title="Suggested for your shop">
        <div className="t-tiny" style={{ marginBottom: 10 }}>
          Tick what you sell. You get your own copy - rename or remove it any time.
        </div>
        {!master.length ? (
          <Empty emoji="🗂️" title="Nothing suggested yet" text="KAAMWALA has not added ready-made categories for your trade." />
        ) : master.map((row) => {
          const on = picked.includes(row._id);
          return (
            <button
              type="button"
              key={row._id}
              className={`option-row ${on ? 'on' : ''}`}
              disabled={row.adopted}
              onClick={() => setPicked(on ? picked.filter((x) => x !== row._id) : [...picked, row._id])}
              style={row.adopted ? { opacity: 0.5 } : undefined}
            >
              <span>{row.adopted ? '✓' : on ? '☑️' : '⬜️'}</span>
              <span>{row.icon || '🗂️'}</span>
              <span className="grow" style={{ minWidth: 0 }}>
                <span className="t-body">{row.name}</span>
                {row.children.length ? (
                  <span className="t-tiny clamp-1" style={{ display: 'block' }}>{row.children.map((k) => k.name).join(', ')}</span>
                ) : null}
              </span>
              {row.adopted ? <span className="t-tiny">Already added</span> : null}
            </button>
          );
        })}
        <div className="row" style={{ gap: 10, marginTop: 12 }}>
          <Button title={picked.length ? `Add ${picked.length}` : 'Tick to add'} disabled={!picked.length} onClick={adopt} />
          <Button title="Close" variant="light" onClick={() => setPicker(false)} />
        </div>
      </Modal>
    </div>
  );
};

export default MyCategoriesPage;
